import { point, lineString, nearestPointOnLine, length, distance as turfDistance } from '@turf/turf';
import { panAmRoute } from './trip-tracking-constants';
import { FUEL_PRICE_BY_COUNTRY, DEFAULT_FUEL_PRICE, VEHICLE_FUEL_CONSUMPTION} from './statistics-fuel-costs-constants';

// Helper to sort steps by startDate
function sortStepsByDate(steps) {
  return [...steps].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
}

// Calculate PanAm progress for a flat array of steps
function getPanAmProgress(userSteps, panAmRoute) {
  if (!userSteps || userSteps.length < 2) return { percent: 0, kmDone: 0, kmTotal: 0 };

  const panAmLine = lineString(panAmRoute);
  const totalRouteDistance = length(panAmLine, { units: 'kilometers' });

  let minLocation = Infinity;
  let maxLocation = -Infinity;

  for (const step of userSteps) {
    const [lng, lat] = step.point.coordinates;
    const pt = point([lng, lat]);
    const snapped = nearestPointOnLine(panAmLine, pt, { units: 'kilometers' });

    const location = snapped.properties.location;
    if (location < minLocation) minLocation = location;
    if (location > maxLocation) maxLocation = location;
  }

  if (minLocation === Infinity || maxLocation === -Infinity) {
    return { percent: 0, kmDone: 0, kmTotal: Math.round(totalRouteDistance) };
  }

  const kmDone = maxLocation - minLocation;
  const percent = (kmDone / totalRouteDistance) * 100;

  return {
    percent: +percent.toFixed(2),
    kmDone: Math.round(kmDone),
    kmTotal: Math.round(totalRouteDistance)
  };
}

// Core function: calculate statistics for a flat array of steps
function calculateStatisticsForSteps(steps, trips, mode = 'global', vehicle="Car") {
  const sortedSteps = sortStepsByDate(steps);

  if (sortedSteps.length === 0) {
    return {
      totalDurationInDays: 0,
      totalTrips: mode === 'trip' ? 1 : trips.length,
      totalSteps: 0,
      countriesVisited: [],
      panAmPercentage: 0,
      panAmKmsDone: 0,
      panAmKmsTotal: panAmRoute.length ? getPanAmProgress([], panAmRoute).kmTotal : 0,
      fuelCostEstimation: 0,
      startDate: null,
      endDate: null,
      startingCountry: null,
      finishingCountry: null
    };
  }

  const countriesVisited = [
    ...new Set(
      sortedSteps
        .map(step => step.countryIsoAlpha2)
        .filter(Boolean)
    )
  ];

  const firstStep = sortedSteps[0];
  const lastStep = sortedSteps[sortedSteps.length - 1];

  const totalDurationInDays = (new Date(lastStep.startDate) - new Date(firstStep.startDate)) / (1000 * 60 * 60 * 24);

  const panAmStats = getPanAmProgress(sortedSteps, panAmRoute);

  let prevStep = null;
  let fuelCostEstimation = 0;
  const consumptionLPer100km = VEHICLE_FUEL_CONSUMPTION[vehicle] || 8;

  for (const step of sortedSteps) {
    const country = step.countryIsoAlpha2 || 'DEFAULT';
    const pricePerLitre = FUEL_PRICE_BY_COUNTRY[country] || DEFAULT_FUEL_PRICE;

    if (prevStep) {
      const from = point(prevStep.point.coordinates);
      const to = point(step.point.coordinates);

      const km = turfDistance(from, to, { units: 'kilometers' });
      fuelCostEstimation += km * (consumptionLPer100km / 100) * pricePerLitre;
    }

    prevStep = step;
  }

  return {
    totalDurationInDays: Math.max(Math.ceil(totalDurationInDays), 0),
    totalTrips: mode === 'trip' ? 1 : trips.length,
    totalSteps: sortedSteps.length,
    countriesVisited,
    panAmPercentage: panAmStats.percent,
    panAmKmsDone: panAmStats.kmDone,
    panAmKmsTotal: panAmStats.kmTotal,
    fuelCostEstimation: Math.round(fuelCostEstimation),
    startDate: firstStep.startDate,
    endDate: lastStep.startDate,
    startingCountry: firstStep.countryIsoAlpha2 || null,
    finishingCountry: lastStep.countryIsoAlpha2 || null
  };
}

// Main buildStatistics function that accepts either a flat array or an array of arrays
export function buildStatistics(stepsInput, trips, mode = 'global', vehicle) {
    // If input is empty or falsy
    if (!stepsInput || stepsInput.length === 0) {
        return {
          totalDurationInDays: 0,
          totalTrips: mode === 'trip' ? 1 : trips.length,
          totalSteps: 0,
          countriesVisited: [],
          panAmPercentage: 0,
          panAmKmsDone: 0,
          panAmKmsTotal: panAmRoute.length ? getPanAmProgress([], panAmRoute).kmTotal : 0,
          startDate: null,
          endDate: null,
          startingCountry: null,
          finishingCountry: null
        };
    }

    // Detect if input is array of arrays (multiple trips)
    const isArrayofArrays = Array.isArray(stepsInput[0]);

    if (!isArrayofArrays) {
        // Single flat array of steps
        return calculateStatisticsForSteps(stepsInput, trips, mode, vehicle);
    }

    // Multiple trips: aggregate statistics
    let totalDuration = 0;
    let totalSteps = 0;
    let countriesSet = new Set();
    let totalKmsDone = 0;
    let totalKmsTotal = 0;
    let fuelCostEstimation = 0;

    let earliestStart = null;
    let earliestStartCountry = null;
    let latestEnd = null;
    let latestEndCountry = null;

    for (const steps of stepsInput) {
        const stats = calculateStatisticsForSteps(steps, trips, mode, vehicle);

        totalDuration += stats.totalDurationInDays;
        totalSteps += stats.totalSteps;
        stats.countriesVisited.forEach(c => countriesSet.add(c));
        totalKmsDone += stats.panAmKmsDone;
        fuelCostEstimation += stats.fuelCostEstimation;

        if (totalKmsTotal === 0) {
          totalKmsTotal += stats.panAmKmsTotal;
        }

        const start = stats.startDate ? new Date(stats.startDate) : null;
        if (start && (!earliestStart || start < earliestStart)) {
            earliestStart = start;
            earliestStartCountry = stats.startingCountry;
        }

        const end = stats.endDate ? new Date(stats.endDate) : null;
        if (end && (!latestEnd || end > latestEnd)) {
            latestEnd = end;
            latestEndCountry = stats.finishingCountry;
        }
    }

    const panAmPercentage = totalKmsTotal ? +((totalKmsDone / totalKmsTotal) * 100).toFixed(2) : 0;

    return {
        totalDurationInDays: Math.max(Math.ceil(totalDuration), 0),
        totalTrips: mode === 'trip' ? 1 : trips.length,
        totalSteps,
        countriesVisited: Array.from(countriesSet),
        panAmPercentage: Math.min(panAmPercentage, 100),
        panAmKmsDone: Math.round(totalKmsDone),
        panAmKmsTotal: Math.round(totalKmsTotal),
        fuelCostEstimation: Math.round(fuelCostEstimation),
        startDate: earliestStart ? earliestStart.toISOString() : null,
        endDate: latestEnd ? latestEnd.toISOString() : null,
        startingCountry: earliestStartCountry || null,
        finishingCountry: latestEndCountry || null
    };
}
